import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { connectionQueue } from '../lib/connectionQueue';
import type { UserAccessInfo } from '../types/billing';
import { devLog, devWarn, logCriticalError, logCriticalWarning } from '../lib/devLog';

export interface AccessCheckResult {
  hasAccess: boolean;
  error: boolean;
  errorMessage?: string;
}

interface CachedAccessCheck {
  result: AccessCheckResult;
  timestamp: number;
  userId: string;
  isGrandfathered?: boolean;
}

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const ERROR_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes for errors
const BACKGROUND_REFRESH_THRESHOLD = 23 * 60 * 60 * 1000; // 23 hours - refresh before expiry
const COOLDOWN_PERIOD = 5 * 60 * 1000; // 5 minutes cooldown between checks
const CACHE_KEY = 'sano_access_check_cache';

// Request deduplication: track in-flight access checks
let inFlightCheckPromise: Promise<AccessCheckResult> | null = null;
let inFlightCheckUserId: string | null = null;
let lastCheckTimestamp: number = 0;

// Helper functions for localStorage cache
function getCachedAccessCheck(): CachedAccessCheck | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached) as CachedAccessCheck;

    // Validate cache structure
    if (!parsed.userId || !parsed.timestamp || !parsed.result) {
      devWarn('[getCachedAccessCheck] Invalid cache structure, clearing');
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    devWarn('[getCachedAccessCheck] Failed to read cache:', error);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

function setCachedAccessCheck(cache: CachedAccessCheck): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    devWarn('[setCachedAccessCheck] Failed to write cache:', error);
  }
}

async function performAccessCheck(userId: string, retryCount = 0): Promise<AccessCheckResult> {
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [500, 1000, 2000]; // 0.5s, 1s, 2s exponential backoff
  const TIMEOUT_MS = 5000; // 5 seconds - matches database statement_timeout

  try {
    // Create AbortController for forceful request cancellation
    const abortController = new AbortController();

    // Set timeout that actually aborts the request
    const timeoutId = setTimeout(() => {
      logCriticalWarning('performAccessCheck', `Aborting request after ${TIMEOUT_MS}ms timeout`);
      abortController.abort();
    }, TIMEOUT_MS);

    // Make RPC call with abort signal and connection queue
    // This prevents connection pool exhaustion by limiting concurrent requests
    const { data, error } = await connectionQueue.enqueue(() =>
      supabase.rpc('has_active_access', {}, {
        signal: abortController.signal,
      })
    );

    // Clear timeout on successful completion
    clearTimeout(timeoutId);

    if (error) {
      logCriticalError('[performAccessCheck] RPC error', error);
      throw error;
    }

    const result: AccessCheckResult = {
      hasAccess: data as boolean,
      error: false,
    };

    // Cache the successful result (without checking grandfathered status to avoid extra query)
    // Grandfathered status will be determined lazily when needed
    const cache: CachedAccessCheck = {
      result,
      timestamp: Date.now(),
      userId,
      isGrandfathered: undefined, // Will be set later if needed
    };
    setCachedAccessCheck(cache);

    devLog('[performAccessCheck] ✓ Access check successful, cached for 24 hours');

    return result;
  } catch (error) {
    // Handle abort specifically
    if (error instanceof Error && error.name === 'AbortError') {
      logCriticalError('[performAccessCheck] Request aborted due to timeout', error);
      throw new Error('Access check timeout - database query took too long');
    }

    // Retry with exponential backoff
    if (retryCount < MAX_RETRIES) {
      const delay = RETRY_DELAYS[retryCount];
      logCriticalWarning('performAccessCheck', `Attempt ${retryCount + 1} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return performAccessCheck(userId, retryCount + 1);
    }

    // All retries exhausted
    throw error;
  }
}

export async function checkUserAccess(userId: string, forceRefresh = false): Promise<AccessCheckResult> {
  // Request deduplication: if check is already in flight for this user, return existing promise
  if (!forceRefresh && inFlightCheckPromise && inFlightCheckUserId === userId) {
    devLog('[checkUserAccess] Using in-flight check for same user');
    return inFlightCheckPromise;
  }

  // Check cache first (unless force refresh)
  const cachedCheck = getCachedAccessCheck();

  if (!forceRefresh && cachedCheck && cachedCheck.userId === userId) {
    const age = Date.now() - cachedCheck.timestamp;

    // Grandfathered users: cache never expires!
    if (cachedCheck.isGrandfathered && cachedCheck.result.hasAccess) {
      devLog('[checkUserAccess] ⚡ Grandfathered user - using permanent cache');
      return cachedCheck.result;
    }

    const maxAge = cachedCheck.result.error ? ERROR_CACHE_DURATION : CACHE_DURATION;

    // If cache is still valid, return it
    if (age < maxAge) {
      // Silent background refresh if cache is old (23+ hours)
      if (!cachedCheck.result.error && age > BACKGROUND_REFRESH_THRESHOLD) {
        devLog('[checkUserAccess] Cache near expiry, refreshing in background...');
        // Fire and forget - don't await
        performAccessCheck(userId).catch(error => {
          devWarn('[checkUserAccess] Background refresh failed:', error);
        });
      }
      return cachedCheck.result;
    }

    // Cache expired, but we'll try to refresh
    devLog('[checkUserAccess] Cache expired, refreshing...');
  }

  // Cooldown check: prevent rapid successive checks
  const timeSinceLastCheck = Date.now() - lastCheckTimestamp;
  if (!forceRefresh && timeSinceLastCheck < COOLDOWN_PERIOD && cachedCheck) {
    const remainingCooldown = Math.ceil((COOLDOWN_PERIOD - timeSinceLastCheck) / 1000);
    devLog(`[checkUserAccess] Cooldown active (${remainingCooldown}s remaining), using cached result`);
    return cachedCheck.result;
  }

  // Check if Supabase is configured
  if (!isSupabaseConfigured()) {
    logCriticalError('[checkUserAccess] Supabase is not configured', new Error('Supabase not configured'));
    return {
      hasAccess: false,
      error: true,
      errorMessage: 'Database connection not configured. Please contact support.',
    };
  }

  // Start new access check
  lastCheckTimestamp = Date.now();
  inFlightCheckUserId = userId;
  inFlightCheckPromise = (async () => {
    try {
      const result = await performAccessCheck(userId);
      return result;
    } catch (error) {
      logCriticalError('[checkUserAccess] Error checking user access after all retries', error);

      // If we have an expired cache with successful access, use it temporarily
      // This allows users to continue working during temporary network issues
      if (cachedCheck && !cachedCheck.result.error && cachedCheck.result.hasAccess) {
        const age = Date.now() - cachedCheck.timestamp;
        const hoursOld = Math.floor(age / (1000 * 60 * 60));
        logCriticalWarning('checkUserAccess', `Using expired cache (${hoursOld}h old) due to network error`);
        return cachedCheck.result;
      }

      // FAIL CLOSED: Deny access on error to prevent security bypass
      const errorMessage = error instanceof Error ? error.message : 'Unable to verify access';
      const result: AccessCheckResult = {
        hasAccess: false,
        error: true,
        errorMessage,
      };

      // Cache error results for 5 minutes to prevent retry storms
      const errorCache: CachedAccessCheck = {
        result,
        timestamp: Date.now(),
        userId,
      };
      setCachedAccessCheck(errorCache);
      devLog('[checkUserAccess] Error result cached for 5 minutes');

      return result;
    } finally {
      // Clear in-flight tracking
      inFlightCheckPromise = null;
      inFlightCheckUserId = null;
    }
  })();

  return inFlightCheckPromise;
}

/**
 * Clears the cached access check result.
 *
 * This is automatically called when:
 * - User logs out
 * - User changes (different user logs in)
 *
 * You can also call this manually after subscription changes to force a refresh.
 */
export function clearAccessCheckCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    devLog('[clearAccessCheckCache] Cache cleared (user changed or logged out)');
  } catch (error) {
    devWarn('[clearAccessCheckCache] Failed to clear cache:', error);
  }
}

export async function getUserAccessInfo(userId: string): Promise<UserAccessInfo | null> {
  try {
    // Use connection queue to prevent pool exhaustion
    const { data, error } = await connectionQueue.enqueue(() =>
      supabase.rpc('get_user_access_info')
    );

    if (error) throw error;
    const accessInfo = data && data.length > 0 ? data[0] : null;

    // Cache grandfathered status if detected
    if (accessInfo?.access_source === 'grandfathered') {
      const cachedCheck = getCachedAccessCheck();
      if (cachedCheck && cachedCheck.userId === userId) {
        cachedCheck.isGrandfathered = true;
        setCachedAccessCheck(cachedCheck);
        devLog('[getUserAccessInfo] ✓ Grandfathered status cached');
      }
    }

    return accessInfo;
  } catch (error) {
    logCriticalError('Error getting user access info', error);
    return null;
  }
}

export async function createCheckoutSession(type: 'individual' | 'organization', quantity?: number) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch(
      type === 'individual'
        ? '/.netlify/functions/create-checkout-session'
        : '/.netlify/functions/create-org-checkout',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          quantity: type === 'organization' ? quantity : undefined,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    logCriticalError('Error creating checkout session', error);
    throw error;
  }
}

export async function createPortalSession() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch('/.netlify/functions/create-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create portal session');
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    logCriticalError('Error creating portal session', error);
    throw error;
  }
}

export async function acceptOrganizationInvite(token: string, userId: string) {
  try {
    const { data, error } = await supabase.rpc('accept_organization_invite', {
      invite_token: token,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    logCriticalError('Error accepting organization invite', error);
    throw error;
  }
}

export async function createOrganizationInvite(organizationId: string, email: string) {
  try {
    const { data, error } = await supabase
      .from('organization_invites')
      .insert({
        organization_id: organizationId,
        email,
        invited_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logCriticalError('Error creating organization invite', error);
    throw error;
  }
}

export async function getOrganizationMembers(organizationId: string) {
  try {
    const { data, error } = await supabase
      .from('organization_members')
      .select(`
        *,
        user:user_id (
          email
        )
      `)
      .eq('organization_id', organizationId);

    if (error) throw error;
    return data;
  } catch (error) {
    logCriticalError('Error getting organization members', error);
    throw error;
  }
}

export async function removeOrganizationMember(memberId: string) {
  try {
    const { error } = await supabase
      .from('organization_members')
      .delete()
      .eq('id', memberId);

    if (error) throw error;
  } catch (error) {
    logCriticalError('Error removing organization member', error);
    throw error;
  }
}

export interface PendingInvite {
  id: string;
  token: string;
  organization: {
    id: string;
    name: string;
  };
  invited_by_profile: {
    email: string;
  } | null;
  expires_at: string;
  created_at: string;
}

export async function getPendingInvites(userEmail: string): Promise<PendingInvite[]> {
  try {
    const { data, error } = await supabase
      .from('organization_invites')
      .select(`
        id,
        token,
        expires_at,
        created_at,
        organization:organization_id (
          id,
          name
        ),
        invited_by_profile:profiles!organization_invites_invited_by_fkey (
          email
        )
      `)
      .eq('email', userEmail.toLowerCase())
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      logCriticalError('Error fetching pending invites', error);
      return [];
    }
    return data || [];
  } catch (error) {
    console.error('Error fetching pending invites:', error);
    return [];
  }
}

export async function cancelIndividualForOrg(organizationId: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch('/.netlify/functions/cancel-individual-for-org', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        organization_id: organizationId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel individual subscription');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logCriticalError('Error cancelling individual subscription', error);
    throw error;
  }
}
